<template>
  <div>
    <div
      :key="service.id"
      :disabled="!isServiceNotLinked(service)"
      :class="[
        {
          'cursor-pointer': isServiceNotLinked(service)
        },
        { '!w-full': onboarding }
      ]"
      class="group/card border-[1px] p-2 overflow-visible w-[300px] h-[230px] sm:min-w-[250px] min-h-[230px] bg-white dark:bg-dark-box-background border-light-divider-border dark:border-dark-divider-border rounded-md flex-grow-0 flex flex-1 flex-row justify-between">
      <div class="relative flex flex-col items-start justify-start w-full text-sm font-medium text-gray-900">
        <div class="flex flex-row p-2 pb-4 border-b w-full justify-between">
          <div class="flex flex-row w-full">
            <div class="rounded-md p-0.5 bg-gray-100 flex items-center justify-center mr-2">
              <img :src="service.logo_url" class="h-7 my-auto mr-2 ml-1">
            </div>
            <div class="flex flex-col">
              <div class="flex flex-row my-auto">
                <span class="pr-1 capitalize dark:text-dark-body-text">{{ service.title }}
                  <span class="text-gray-400 text-xsm">{{
                    $store.state.integration.integrations_conn_stats[service.origin]?.status
                  }}</span></span>
              </div>
              <span v-if="service.instance_number > 1"
                    class="font-bold text-gray-500 capitalize font-sm">Instance: {{ service.instance_number }}</span>
              <span class="font-light text-gray-500 capitalize font-sm">{{
                service?.category?.toLowerCase() == "crm" ? "CRM" : service.category.replace("_", " ")
              }}</span>
            </div>
          </div>
          <button v-if="service.status == 'pending'" type="button" class="btn-danger w-fit" @click.stop="deleteService(service)">
            <span class="w-full text-center whitespace-nowrap flex-nowrap"> {{ $t("Cancel") }} </span>
          </button>
        </div>
        <div class="flex flex-col h-full justify-start space-y-2 pt-2">
          <div v-if="service.status !== 'on'" class="text-common-grey">
            {{ $t("Not connected") }}
          </div>
          <div v-if="service.status == 'on'" class="text-xs font-normal flex items-center">
            <CircleCheck class="w-4 mr-2 h-4 text-green-400" aria-hidden="true" />
            {{
              service.reference == "gmail" || service.reference == "outlook"
                ? service.user_email?.toLowerCase()
                : $utils.getUserById(service.created_by)?.email?.toLowerCase()
            }}
          </div>
          <div
            v-if="service.last_edited && service.status == 'on' && (service.name == 'salesforce' || service.title == 'salesforce')"
            class="font-normal text-xs flex items-center">
            <Clock class="w-4 h-4 mr-2" /> <span class="font-medium mr-2">{{ $t("Last sync") }}: </span>
            {{ $moment(service.last_edited).fromNow() }}
          </div>
          <div v-if="services_realtime_updates_status[service.name]?.status == 'on'" class="text-xs font-normal flex items-center">
            <ArrowLeftRight class="w-4 h-4 mr-2" color="#27C884" /> {{ $t("Two way sync set up") }}
          </div>
        </div>

        <SimpleActionDropDown
          class="!absolute top-0 right-0 flex-shrink-0"
          :icon="EllipsisVertical"
          item-class="flex items-start w-full px-2 py-2 whitespace-nowrap"
          :icon-button="true"
          :items="getServiceActions(service)" />
        <div
          v-if="!integrations_sync_stats[service.origin]?.object_stats[integrations_sync_stats[service.origin]?.current_object]"
          class="absolute flex-col gap-2 bottom-2 ml-[10%] w-[80%] flex justify-end">
          <button
            :class="[
              { '!rounded-full text-white': service.status !== 'off' },
              { 'bg-status-success-green hover:bg-status-success-green border-status-success-green': service.status == 'on' },
              { 'bg-status-error-red hover:bg-status-error-red border-status-error-red': service.status == 'revoked' },
              {
                'bg-status-warning-yellow hover:bg-status-warning-yellow border-status-warning-yellow': service.status == 'pending'
              }
            ]"
            type="button"
            class="inline-flex cancel-btn items-center px-4 py-1 transition-all border rounded-md hover:scale-105 hover:cursor-pointer dark:bg-dark-screen-background focus:outline-none"
            @click.stop="linkService(service)">
            <span class="w-full flex justify-center items-center flex-wrap text-center leading-tight whitespace-normal">
              <Plug v-if="service.status == 'off'" class="w-4 h-4 mr-2" />
              {{ service_status_message }}
            </span>
          </button>
        </div>

        <div v-if="isSyncInProgress(service)">
          <div class="relative flex items-center justify-center px-2 mb-2 font-medium bg-gray-800 rounded-md cursor-pointer has-tooltip">
            <span class="px-2 py-1 font-medium text-white capitalize truncate text-xsm w-fit whitespace-nowrap">
              {{ getSyncCurrentObject(service) }}
            </span>

            <!-- Progress percentage display -->
            <span v-if="shouldShowSyncPercentage(service)" class="w-10 h-6 py-0.5 text-white right-2" aria-hidden="true">
              {{ getSyncProgressPercentage(service) }}%
            </span>

            <!-- Loading indicator -->
            <span v-else-if="shouldShowSyncLoading(service)" class="w-10 h-6 py-0.5 text-white right-2" aria-hidden="true"> ... </span>

            <!-- Synced count only -->
            <span v-else-if="shouldShowSyncCount(service)" class="w-10 h-6 py-0.5 text-white right-2" aria-hidden="true">
              {{ getSyncCurrentObjectSynced(service) }}
            </span>

            <!-- Tooltip with detailed sync status -->
            <div class="min-w-[500px] flex-wrap mt-1 text-gray-800 bg-white rounded shadow-xl tooltip whitespace-nowrap left-5 top-5">
              <div class="p-4 bg-white rounded-md">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <CircleCheck class="w-8 h-8 text-green-400" aria-hidden="true" />
                  </div>
                  <div class="ml-3">
                    <h3 class="font-medium text-gray-800 text-md">
                      {{ $t("Data Sync Status") }}
                    </h3>
                    <br>
                    <template v-for="obj in getSyncObjects(service)" :key="obj">
                      <strong>{{ obj }}</strong>: ({{ getObjectSyncedCount(service, obj) }}
                      /
                      {{ getObjectTotalCount(service, obj) }})
                      <br>
                    </template>
                  </div>
                </div>
              </div>
              <div class="flex justify-between w-full p-2 mt-2 mb-1">
                <button class="w-1/2 danger-btn" @click.stop="() => stopSyncing(service)">
                  {{ $t("Stop Syncing") }}
                </button>
                <button
                  class="w-1/2 cancel-btn"
                  @click.stop="() => $store.commit('integration/removeIntegrationSyncStatus', service.origin)">
                  {{ $t("Close progress") }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="service.status == 'on' && services_realtime_updates_status[service.origin]?.status == 'off'">
          <div class="relative flex items-center justify-center px-2 font-medium bg-yellow-500 rounded-full cursor-pointer has-tooltip">
            <span
              v-if="services_realtime_updates_status[service.origin]?.checking_for_status"
              class="px-2 py-1 font-medium text-white truncate text-xsm w-fit whitespace-nowrap">{{ $t("Checking...") }}
            </span>
            <span v-else class="px-2 py-1 font-medium text-white truncate text-xsm w-fit whitespace-nowrap">{{ $t("Sync") }} OFF</span>
            <TriangleAlert class="w-6 h-6 text-white right-2" aria-hidden="true" />

            <div class="flex-wrap mt-1 text-gray-800 bg-white rounded shadow-xl tooltip whitespace-nowrap left-5 top-5">
              <div class="p-4 bg-white rounded-md">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <TriangleAlert class="w-8 h-8 text-yellow-400" aria-hidden="true" />
                  </div>
                  <div class="ml-3">
                    <h3 class="text-lg font-medium text-yellow-800">
                      {{ $t("RevSetter was unable to establish two way sync!") }}
                    </h3>
                    <div class="mt-2 text-sm text-yellow-700">
                      {{ $t("Any New Records or updates from your") }}
                      {{ service.title }} {{ $t("account will not be synced into RevSetter.") }} <br>{{
                        $t("Please ensure that following are checked")
                      }}:
                      <div class="mt-2 text-sm text-yellow-700">
                        <ul role="list" class="pl-5 space-y-1 list-disc w-fit">
                          <li>
                            {{ $t("You have the Streaming API enabled") }}
                            <CircleCheck
                              v-if="services_realtime_updates_status[service.origin]?.is_streaming_api_on"
                              class="float-right w-5 h-5 text-green-400" />
                            <CircleX v-else class="float-right w-5 h-5 text-red-400" />
                          </li>
                          <li>
                            {{ $t("User") }}
                            {{ $utils.getUserById(service.created_by)?.full_name }}
                            {{ service.created_by == $store.state.auth.user.id ? $t("(You)") : "" }}
                            {{ $t("have the necessary permissions to utilize it.") }}
                            <CircleCheck
                              v-if="services_realtime_updates_status[service.origin]?.user_has_permissions"
                              class="float-right w-5 h-5 text-green-400" />
                            <CircleX v-else class="float-right w-5 h-5 text-red-400" />
                          </li>
                        </ul>
                      </div>
                      <br>
                      <strong>{{ $t("Account") }}</strong>: {{ service.instance_url }}
                      <br>
                      <strong>{{ $t("Email") }}</strong>:
                      {{ $utils.getUserById(service.created_by)?.email }}
                    </div>
                    <div class="flex justify-end w-full">
                      <button
                        class="text-center text-yellow-700 bg-transparent border-none cancel-btn hover:bg-yellow-400"
                        @click.stop="
                          () => {
                            getServiceRealtimeUpdatesStatus(service)
                          }
                        ">
                        {{ $t("Check") }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else-if="(service.status == 'on' && service.category == 'email') || service.category == 'calendar'"
          class="transition-all duration-200 delay-200 opacity-0 group-hover:opacity-100">
          <div class="relative flex items-center justify-center font-medium !bg-white cursor-pointer has-tooltip">
            <CircleAlert class="w-6 h-6 text-green-400 right-2" aria-hidden="true" />

            <div class="flex-wrap mt-1 text-gray-800 bg-white rounded shadow-xl tooltip whitespace-nowrap left-5 top-5">
              <div class="p-4 bg-white rounded-md">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <CircleCheck class="w-8 h-8 text-green-400" aria-hidden="true" />
                  </div>
                  <div class="ml-3">
                    <h3 class="text-lg font-medium text-gray-800">
                      {{ $t("Your") }} {{ service.title }} {{ $t("is linked") }}
                    </h3>
                    <br>
                    <strong>{{ $t("Email") }}</strong>: {{ $utils.getUserById(service.created_by)?.email }}<br>
                    <strong>{{ $t("Linked on") }}</strong>:
                    {{ $moment(service.datetime_created).format("MMM DD, YYYY") }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal v-model:is-open="show_revoke_confirmation">
      <div class="sm:flex sm:items-start">
        <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
          <TriangleAlert class="w-8 h-8 text-red-600" aria-hidden="true" />
        </div>
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <label as="h3" class="text-lg font-medium leading-6 text-gray-900"> {{ $t("Revoke") }} {{ selected_service?.title }} </label>
          <div class="mt-2">
            <p class="text-gray-500 text-md">
              {{ $t("Are you sure you want to revoke RevSetter access to") }}
              {{ selected_service?.title }}? <br>{{ $t("This Will prevent syncing between your") }} {{ selected_service?.title }}
              {{ $t("and RevSetter! and you will have to re-authenticate your") }} {{ selected_service?.title }} {{ $t("again.") }}
            </p>
          </div>
        </div>
      </div>
      <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
          @click="
            () => {
              revokeService(selected_service)
              show_revoke_confirmation = false
            }
          ">
          {{ $t("Revoke") }}
        </button>
        <button
          ref="cancelButtonRef"
          type="button"
          class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark sm:mt-0 sm:w-auto sm:text-sm"
          @click="show_revoke_confirmation = false">
          {{ $t("Cancel") }}
        </button>
      </div>
    </Modal>

    <Modal v-model:is-open="show_disable_confirmation">
      <div class="sm:flex sm:items-start">
        <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
          <TriangleAlert class="w-8 h-8 text-red-600" aria-hidden="true" />
        </div>
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <label as="h3" class="text-lg font-medium leading-6 text-gray-900"> {{ $t("Disable") }} {{ selected_service?.title }} </label>
          <div class="mt-2">
            <p class="text-gray-500 text-md">
              {{ $t("Are you sure you want to disable your") }}
              {{ selected_service?.title }} {{ $t("Account in RevSetter?") }} <br>{{ $t("This Will prevent syncing between your") }}
              {{ selected_service?.title }} {{ $t("Account and RevSetter!") }}.
            </p>
          </div>
        </div>
      </div>
      <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
          @click="
            () => {
              deactivateService(selected_service)
              show_disable_confirmation = false
            }
          ">
          {{ $t("Disable") }}
        </button>
        <button
          ref="cancelButtonRef"
          type="button"
          class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark sm:mt-0 sm:w-auto sm:text-sm"
          @click="show_disable_confirmation = false">
          {{ $t("Cancel") }}
        </button>
      </div>
    </Modal>

    <Modal v-model:is-open="show_confirm_purge_input_modal">
      <div class="sm:flex sm:items-start">
        <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
          <TriangleAlert class="w-6 h-6 text-red-600" aria-hidden="true" />
        </div>
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <label as="h3" class="text-lg font-medium leading-6 text-gray-900">
            {{ $t("Delete") }} {{ selected_service?.title }}
            {{ $t("Records") }}
          </label>
          <div class="flex flex-col mt-2">
            <template v-if="purge_data_step === 0">
              <p class="font-semibold text-md">
                {{ $t("Are you sure you want to delete all records related to your") }}
                {{ selected_service?.title }} {{ $t("instance?") }}
              </p>
              <div class="font-semibold text-red-500 text-md">
                {{ $t("Note: This will delete all records related to your") }}
                {{ selected_service?.title }} {{ $t("instance with any related entities Which includes") }}:
                <ul role="list" class="pl-5 space-y-1 list-disc w-fit">
                  <li>{{ $t("All related interactions (emails, calls, meetings, etc.)") }}</li>
                  <li>{{ $t("All related tasks") }}</li>
                  <li>{{ $t("All related goals") }}</li>
                  <li>{{ $t("All related plans") }}</li>
                </ul>
              </div>
            </template>
            <template v-else-if="purge_data_step === 1">
              <p class="pt-3 text-gray-500 text-md">
                {{ $t("To confirm please enter the confirmation Pin code that has been sent to your Email") }}
              </p>
              <input
                v-model="purge_pin_code"
                class="font-bold text-center text-lg appearance-none block w-full px-3 py-2 border border-light-divider-border dark:border-dark-divider-border rounded-md shadow-sm placeholder:first-letter:capitalize placeholder-common-grey text-light-body-text dark:text-dark-body-text focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue transition ease-in-out duration-200 min-h-[42px]">
              <span v-if="invalid_pin_code" class="text-red-500 text-md">{{ $t("Invalid Pin") }}</span>
            </template>
          </div>
        </div>
      </div>
      <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
          @click="
            () => {
              purge_data_step === 1 ? confirmClearServiceData(selected_service) : (purge_data_step = 1)
            }
          ">
          {{ purge_data_step === 1 ? $t("Delete All") + ` ${selected_service?.title} ` + $t("related records") : $t("Confirm") }}
        </button>
        <button
          ref="cancelButtonRef"
          type="button"
          class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark sm:mt-0 sm:w-auto sm:text-sm"
          @click="show_confirm_purge_input_modal = false">
          {{ $t("Cancel") }}
        </button>
      </div>
    </Modal>

    <DataSyncJobs v-model:is-open="show_sync_jobs_modal" :service="selected_service" />

    <Modal v-model:is-open="show_prefill_whatsapp_business_modal">
      <template #form>
        <div class="w-full p-6">
          <h3 class="text-xl font-semibold text-gray-900 mb-4">
            {{ $t("WhatsApp Business Account") }}
          </h3>
          <Form
            :validation-schema="validation_schema"
            :initial-values="prefill_details"
            class="grid grid-cols-1 gap-4 sm:grid-cols-2"
            @submit="submitWhatsAppBusinessPrefillDetails">
            <TextInput
              :label="$t('Business Name')"
              :classes="'col-span-2'"
              input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              name="business_name"
              type="text"
              :placeholder="$t('Enter your business name')" />

            <TextInput
              :label="$t('Business Email')"
              input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              name="business_email"
              type="email"
              :placeholder="$t('Enter your business email')" />

            <TextInput
              :label="$t('Business Website')"
              input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              name="business_website"
              type="url"
              :placeholder="$t('Enter your business website')" />

            <TextInput
              :label="$t('Business Phone')"
              input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              name="business_phone_number"
              type="tel"
              :placeholder="$t('Enter your business phone number')" />

            <TextInput
              :label="$t('Country Code')"
              input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              name="country_code"
              type="number"
              :placeholder="$t('Enter your country code')" />

            <TextInput
              :label="$t('Business Address')"
              :classes="'col-span-2'"
              input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              name="business_address"
              type="text"
              :placeholder="$t('Enter your business address')" />

            <button type="submit" class="primary-btn col-span-2">
              {{ $t("Submit") }}
            </button>
          </Form>
        </div>
      </template>
    </Modal>
    <Modal v-model:is-open="show_make_service_public_or_private_modal">
      <div class="sm:flex sm:items-start">
        <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-yellow-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
          <TriangleAlert class="w-8 h-8 text-yellow-600" aria-hidden="true" />
        </div>
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <label as="h3" class="text-lg font-medium leading-6 text-gray-900">
            {{ $t("Make Service Public") }} {{ selected_service?.title }}
          </label>
          <div class="mt-2">
            <p class="text-gray-500 text-md">
              {{ $t("Are you sure you want to make your") }}
              <span v-if="selected_service?.is_public">
                {{ selected_service?.title }} {{ $t("Account public in RevSetter?") }} <br>{{ $t("This Will allow your team to use it") }}
              </span>
              <span v-else>
                {{ selected_service?.title }} {{ $t("Account Private in RevSetter!") }} <br>{{
                  $t("This Will Prevent your team to use it")
                }}
              </span>
            </p>
          </div>
        </div>
      </div>
      <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-yellow-600 border border-transparent rounded-md shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm"
          @click="
            () => {
              togglePublicPrivateStatus(selected_service)
            }
          ">
          {{ selected_service?.is_public ? $t("Make Private") : $t("Make Public") }}
        </button>
        <button
          ref="cancelButtonRef"
          type="button"
          class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark sm:mt-0 sm:w-auto sm:text-sm"
          @click="show_make_service_public_or_private_modal = false">
          {{ $t("Cancel") }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import {
  Trash2,
  EllipsisVertical,
  Link,
  CircleCheck,
  Layers,
  TriangleAlert,
  CircleAlert,
  SlidersVertical,
  CircleX,
  KeyRound,
  CloudDownload,
  Clock,
  Plug,
  ArrowLeftRight
} from "lucide-vue-next"
import IntegrationService from "@/services/integration.service"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import SimpleActionDropDown from "@/components/shared/dropdowns/SimpleActionDropDown.vue"
import Modal from "@/components/shared/Modal.vue"
import { getAuthUrl } from "@/views/dashboard/admin/integrations/oauth.js"
import DataSyncJobs from "@/views/dashboard/admin/integrations/info/DataSyncJobs.vue"
import { getSchema } from "./ml"
import { googleSdkLoaded } from "vue3-google-login"
import * as Yup from "yup"
import { Form } from "vee-validate"
import TextInput from "@/components/shared/TextInput.vue"
import { utils } from "@/store/helpers"

export default {
  name: "IntegrationsList",
  components: {
    DataSyncJobs,
    Plug,
    Clock,
    ArrowLeftRight,
    Modal,
    SimpleActionDropDown,
    TriangleAlert,
    CircleCheck,
    CircleAlert,
    CircleX,
    Form,
    TextInput
  },
  props: {
    paidSub: null,
    subscribeToScribe: null,
    service: {
      type: Object,
      required: true
    },
    allServices: {
      type: Array,
      required: true
    },
    onboarding: null
  },
  emits: ["update:allServices", "update:service", "service-deleted"],
  data() {
    return {
      CloudDownload,
      EllipsisVertical,
      services_realtime_updates_status: {},
      available_services: [],
      current_file: "",
      show_make_service_public_or_private_modal: false,
      show_verify_whatsapp_number_modal: false,
      show_sync_jobs_modal: false,
      show_confirm_purge_input_modal: false,
      show_revoke_confirmation: false,
      show_disable_confirmation: false,
      invalid_pin_code: false,
      purge_data_step: 0,
      purge_pin_code: null,
      selected_service: null,
      integration_name: null,
      whatsapp_number: "",
      service_clone: null,
      interval_fetch: null,
      show_prefill_whatsapp_business_modal: false,
      prefill_details: {
        business_name: "",
        business_email: "",
        business_website: "",
        business_phone_number: "",
        country_code: "",
        business_address: ""
      },
      validation_schema: Yup.object().shape({
        business_name: Yup.string().required(this.$t("Business Name is required")),
        business_email: Yup.string().email(this.$t("Invalid email")).required(this.$t("Business Email is required")),
        business_website: Yup.string().url(this.$t("Invalid URL")).required(this.$t("Business Website is required")),
        business_phone_number: Yup.string().required(this.$t("Business Phone is required")),
        country_code: Yup.number().required(this.$t("Country Code is required")),
        business_address: Yup.string().required(this.$t("Business Address is required"))
      })
    }
  },
  computed: {
    service_status_message() {
      const { status, reference } = this.service_clone
      if (status === "on") {
        return this.$t("Connected")
      } else if (status === "revoked") {
        return this.$t("Revoked")
      } else if (status === "pending") {
        return this.$t("Pending")
      } else if (reference == "spreadsheet") {
        return this.$t("Upload your file")
      } else if (reference == "whatsapp") {
        return this.$t("Link/Create WhatsApp Business Account")
      } else {
        // if database use link to instance
        if (this.service_clone.category == "database") {
          return this.$t("Link your database")
        }

        return this.$t("Link your account")
      }
    },

    integrations_sync_stats() {
      return this.$store.state.integration.integrations_sync_stats
    }
  },
  watch: {
    show_sync_jobs_modal: {
      handler(val) {
        if (!val) {
          clearInterval(this.interval_fetch)
        }
      }
    },
    available_services: {
      deep: true,
      handler() {
        this.$emit("update:allServices", this.available_services)
      }
    }
  },
  created() {
    const url_integration = this.$route.query?.integration

    if (url_integration === this.service.reference) {
      this.linkService(this.service, true)
    }
    this.integration_name = this.service.name
    this.registerListeners()
    this.refreshServicesRealtimeUpdatesStatus(this.service)
    this.available_services = this.allServices
    this.service_clone = { ...this.service }
  },
  deactivated() {
    this.unregisterListeners()
  },
  unmounted() {
    this.unregisterListeners()
  },
  methods: {
    registerListeners() {
      this.$emitter.on(ALL_EMITTERS.CANCEL_CSV_UPLOAD, () => this.resetFile())
    },
    unregisterListeners() {
      this.$emitter.off(ALL_EMITTERS.CANCEL_CSV_UPLOAD)
    },

    refreshServicesRealtimeUpdatesStatus() {
      this.available_services?.forEach((service) => {
        if (service.reference == "salesforce" && service.integration_provider == "revsetter" && service.status == "on") {
          this.getServiceRealtimeUpdatesStatus(service)
        }
      })
    },
    getServiceRealtimeUpdatesStatus(service) {
      if (service.status == "on") {
        const service_name = service.name
        this.$data["services_realtime_updates_status"][service_name] = {
          checking_for_status: true
        }

        IntegrationService.getServiceRealtimeUpdatesStatus(service_name).then((res) => {
          this.$data["services_realtime_updates_status"][service_name] = {
            ...res.data,
            checking_for_status: false
          }
        })
      }
    },
    getServiceActions(service) {
      let actions_mapper = {
        upload: {
          name: this.$t("Upload New File"),
          icon: Link,
          action: () => this.linkService(service)
        },
        "re-upload": {
          name: this.$t("Re-Upload"),
          icon: Link,
          action: () => this.$router.push(`/${service.reference}/${service.id}/spreadsheet/upload`)
        },
        "make-public": {
          name: service.is_public ? this.$t("Make Private") : this.$t("Make Public"),
          icon: Link,
          action: () => this.showMakeServicePublicOrPrivate(service)
        },
        jobs: {
          name: this.$t("Data Syncing Jobs"),
          icon: CloudDownload,
          action: () => {
            this.selected_service = service
            this.show_sync_jobs_modal = true
          }
        },
        "re-auth": {
          name: this.$t("Re-Authenticate"),
          icon: Link,
          action: () => this.reAuth(service)
        },
        "verify-number": {
          name: "Verify WhatsApp Number",
          icon: Link,
          action: () => this.verifyWhatsAppNumber(service)
        },
        revoke: {
          name: this.$t("Revoke Access"),
          icon: KeyRound,
          action: () => this.revokeBtnClick(service)
        },
        disable: {
          name: this.$t("Disable"),
          icon: Link,
          action: () => this.disableBtnClick(service)
        },
        add: {
          name: this.$t("Link New Instance"),
          icon: Link,
          help_text: `This allow you to link a new ${service.reference.title()} instance on top of the existing one`,
          action: () => this.addNewInstance(service)
        },
        link: {
          name:
            {
              slack: this.$t("Link Slack"),
              whatsapp: this.$t("Link Existing WhatsApp Account"),
              default: this.$t("Link")
            }[service.reference] || this.$t("Link"),
          icon: Link,
          help_text: `This will allow RevSetter to act on your behalf in Slack`,
          action: () => {
            const actions = {
              slack: () =>
                IntegrationService.linkService(service.reference, {
                  integration_provider: "revsetter"
                }).then(({ data }) => {
                  let state = btoa(
                    JSON.stringify({
                      next: `/admin/integrations/`,
                      integration_id: data.id,
                      org_id: this.$store.state.organization.id,
                      integration_reference: service.reference
                    })
                  )
                  this.linkSlack(service, "user", state)
                }),
              whatsapp: () => this.linkExistingWhatsAppAccount(),
              default: () => this.linkService(service)
            }
            return (actions[service.reference] || actions.default)()
          }
        },
        new: {
          name: "Create New WhatsApp Business Account",
          icon: Link,
          action: () => this.createNewWhatsAppBusinessAccount(service)
        },
        // mainly for slack for now
        "link-bot": {
          name: this.$t("Link Slack & RevSetter Slack Bot"),
          icon: Link,
          help_text: `This will allow RevSetter Slack Bot to send you notifications and updates via Slack`,
          action: () => {
            IntegrationService.linkService(service.reference, {
              integration_provider: "revsetter"
            }).then(({ data }) => {
              let state = btoa(
                JSON.stringify({
                  next: `/admin/integrations/`,
                  integration_id: data.id,
                  org_id: this.$store.state.organization.id,
                  integration_reference: service.reference
                })
              )
              this.linkSlack(service, "link-bot", state)
            })
          }
        },
        enable: {
          name: this.$t("Activate"),
          icon: Link,
          action: () => this.activateService(service)
        },
        mapping: {
          name: this.$t("Adjust Mapping"),
          icon: SlidersVertical,
          action: () => this.adjustMapping(service)
        },
        resync: {
          name: this.$t("ReSync"),
          icon: Layers,
          action: () => this.reSyncService(service)
        },
        purge: {
          name: this.$t("Delete Data"),
          icon: Trash2,
          action: () => this.clearServiceData(service),
          class: "flex items-center w-full px-2 py-2 text-red-500 hover:bg-red-500 hover:text-white"
        },
        delete: {
          name: this.$t("Delete"),
          icon: Trash2,
          action: () => this.deleteService(service),
          class: "flex items-center w-full px-2 py-2 text-red-500 hover:bg-red-500 hover:text-white"
        },
        info: {
          name: this.$t("Info"),
          icon: CircleAlert,
          action: () => this.getServiceInfo(service)
        },
        separator: {
          name: "separator",
          isSeparator: true,
          class: "border-t border-gray-200 my-1"
        }
      }
      let actions = []
      if (service.status === "on") {
        actions = service.on_actions?.map((action) => actions_mapper[action])
      } else if (service.status === "off" || service.status === "revoked") {
        actions = service.off_actions?.map((action) => actions_mapper[action])
      } else if (service.status === "pending") {
        // For pending status, use off_actions and ensure delete is included for spreadsheet integrations
        actions = service.off_actions?.map((action) => actions_mapper[action]) || []
        if (service.reference === "spreadsheet") {
          // Add delete action if not already included
          if (!actions.some((action) => action?.name === this.$t("Delete"))) {
            actions.push(actions_mapper["delete"])
          }
        }
      } else if (service.status === "disabled") {
        actions.push(actions_mapper["enable"])
      }
      return actions
    },
    verifyWhatsAppNumber(service) {
      this.selected_service = service
      this.whatsapp_number = service?.metadata?.verified_phone_number?.display_phone_number || ""
      this.show_verify_whatsapp_number_modal = true
    },
    createNewWhatsAppBusinessAccount(setup = {}) {
      getAuthUrl("whatsapp", { is_new_wa_signup: true, extra_params: setup }).then((auth_url) => {
        window.open(auth_url, "_blank")
      })
    },
    linkExistingWhatsAppAccount() {
      getAuthUrl("whatsapp", { is_new_wa_signup: false }).then((auth_url) => {
        window.open(auth_url, "_blank")
      })
    },
    disableBtnClick(service) {
      this.selectService(service)
      this.show_disable_confirmation = true
    },
    revokeBtnClick(service) {
      this.selectService(service)
      this.show_revoke_confirmation = true
    },
    reSyncService(service) {
      IntegrationService.reSyncServiceObjects(service.id).then(() => {
        this.showToast(this.$t("ReSync of") + ` ${service.title} ` + this.$t("has been started"), "success")
      })
    },
    isSyncInProgress(service) {
      // if one of the objects status is in progress
      return (
        Object.values(this.integrations_sync_stats[service.origin]?.object_stats || {}).some((obj) => obj.status == "progress") || false
      )
    },
    // Get the current object being synced
    getSyncCurrentObject(service) {
      return this.integrations_sync_stats[service.origin]?.current_object || ""
    },
    // Get the number of synced items for the current object
    getSyncCurrentObjectSynced(service) {
      const currentObject = this.getSyncCurrentObject(service)
      return this.integrations_sync_stats[service.origin]?.[currentObject]?.synced || 0
    },
    // Get the current object stats
    getSyncCurrentObjectStats(service) {
      const currentObject = this.getSyncCurrentObject(service)
      return this.integrations_sync_stats[service.origin]?.object_stats?.[currentObject] || null
    },
    // Determine if we should show the percentage
    shouldShowSyncPercentage(service) {
      const stats = this.getSyncCurrentObjectStats(service)
      return stats && stats.total > 0
    },
    // Calculate the progress percentage
    getSyncProgressPercentage(service) {
      const stats = this.getSyncCurrentObjectStats(service)
      if (!stats || stats.total <= 0) {
        return 0
      }
      return Math.round((stats.synced / stats.total) * 100)
    },
    // Determine if we should show the loading indicator
    shouldShowSyncLoading(service) {
      const stats = this.getSyncCurrentObjectStats(service)
      return stats && stats.total === 0 && stats.synced >= 0
    },
    // Determine if we should show just the count
    shouldShowSyncCount(service) {
      const stats = this.getSyncCurrentObjectStats(service)
      return stats && stats.total === 0
    },
    // Get the list of objects being synced
    getSyncObjects(service) {
      return this.integrations_sync_stats[service.origin]?.objects || []
    },
    // Get the synced count for a specific object
    getObjectSyncedCount(service, objectName) {
      return this.integrations_sync_stats[service.origin]?.object_stats?.[objectName]?.synced || 0
    },
    // Get the total count for a specific object
    getObjectTotalCount(service, objectName) {
      return this.integrations_sync_stats[service.origin]?.object_stats?.[objectName]?.total || 0
    },
    refetchService(service) {
      IntegrationService.getService(service.id).then((res) => {
        this.selected_service = res.data
      })
    },
    showMakeServicePublicOrPrivate(service) {
      this.show_make_service_public_or_private_modal = true
      this.selected_service = service
    },
    togglePublicPrivateStatus() {
      IntegrationService.makeServicePublic(this.selected_service.id, {
        is_public: !this.selected_service.is_public
      })
        .then(() => {
          this.showToast(this.$t("Service has been made public"), "success")
          this.show_make_service_public_or_private_modal = false
          this.selected_service.is_public = !this.selected_service.is_public
        })
        .catch(() => {
          this.showToast(this.$t("Un-able to switch service status. Please try again later"), "error")
        })
    },
    getServiceInfo(service) {
      this.showLoading(`Checking ${service.title} status...`)
      IntegrationService.getServiceInfo(service.id)
        .then((res) => {
          const { scopes, ...otherFields } = res.data

          // Function to format values properly
          const formatValue = (value) => {
            if (Array.isArray(value)) {
              return `<ul class="list-disc pl-4">${value.map((item) => `<li>${item}</li>`).join("")}</ul>`
            } else if (!isNaN(Date.parse(value))) {
              return new Date(value).toLocaleDateString() // Format as readable date
            } else {
              return value
            }
          }

          // Generate formatted key-value pairs
          let formattedFields = Object.entries(otherFields).map(
            ([key, value]) => `
          <div class="flex items-start gap-2">
            <strong class="capitalize w-32 text-white">${key.replace(/_/g, " ")}</strong> 
            <span class="text-white">${formatValue(value)}</span>
          </div>
        `
          )

          // Append Scopes if they exist
          if (scopes) {
            formattedFields.push(`
          <div class="flex items-start gap-2">
            <strong class="capitalize w-32 text-white">Scopes</strong> 
            <span class="text-white">${formatValue(scopes)}</span>
          </div>
        `)
          }

          // Create a styled card for better UI
          const info = `
        <div class="p-4 rounded-lg shadow-md max-h-[60vh] overflow-y-auto custom-scrollbar">
          <h3 class="text-lg font-semibold text-white mb-2">Information</h3>
          <div class="space-y-2">${formattedFields.join("")}</div>
        </div>
      `
          this.hideLoading()

          // Display the formatted data in a toast
          this.$toast.open({
            message: info,
            position: "top",
            type: "default",
            duration: 0
          })
        })
        .catch((err) => {
          this.hideLoading()
          this.$toast.open({
            message: err.response?.data?.description || "An error occurred",
            position: "top",
            type: "error"
          })
        })
    },

    deactivateService(service) {
      this.showLoading(`De-Activating ${service.title}`)
      this.runServiceAction(service, IntegrationService.deactivateService)
    },
    deleteService(service) {
      this.showLoading(`Deleting ${service.title}`)
      IntegrationService.deleteService(service.id)
        .then(() => {
          this.hideLoading()
          // remove from list of services
          this.available_services = this.available_services.filter((s) => s.id !== service.id)

          // Emit an event to notify the parent component to refresh its data
          this.$emit("service-deleted", service.id)

          // Show success message
          this.showToast(this.$t("Service deleted successfully"), "success")
        })
        .catch((error) => {
          this.hideLoading()
          this.showToast(error.response?.data?.description || this.$t("Failed to delete service"), "error")
        })
    },
    revokeService(service) {
      this.showLoading(this.$t("Revoking RevSetter access to your") + ` ${service.title} ` + this.$t("account, Please wait..."))
      if (service.service_provider == "Google") {
        this.runServiceAction(service, IntegrationService.revokeService, () => {
          // request new access token
          let desired_scopes = this.available_services
            .filter((ser) => ser.service_provider == "Google" && ser.status == "on")
            .map((ser) => ser.scope)
            .join(" ")

          googleSdkLoaded((google) => {
            google.accounts.oauth2
              .initCodeClient({
                client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
                scope: desired_scopes,
                access_type: "offline",
                select_account: true,
                ux_mode: "redirect",
                redirect_uri: `${window.location.origin}/integrations/gmail/callback`
              })
              .requestCode()
          })
        })
      } else {
        IntegrationService.revokeService(service.id).then(({ data }) => {
          this.hideLoading()
          // replace service in list
          utils.replaceItem(this.available_services, data, "id")
        })
      }
    },
    activateService(service) {
      this.showLoading(this.$t("Activating") + ` ${service.title}`)
      this.runServiceAction(service, IntegrationService.activateService)
    },
    stopSyncing(service) {
      IntegrationService.stopServiceSync(service.id).then(() => {
        this.$store.commit("integration/removeIntegrationSyncStatus", service.origin)
      })
    },
    clearServiceData(service) {
      this.selectService(service)
      IntegrationService.requestServiceDataPurge(service.name).then(() => {
        this.show_confirm_purge_input_modal = true
      })
    },
    isServiceNotLinked(service) {
      return service.status != "revoked" && service.status != "pending" && service.status != "on"
    },
    linkSlack(service, type, state) {
      let redirectUriBase = window.location.origin
      if (import.meta.env.MODE === "development") {
        redirectUriBase = window.location.origin.replace("http:", "https:")
      }
      const baseUrl = "https://slack.com/oauth/v2/authorize"
      const clientId = import.meta.env.VITE_SLACK_CLIENT_ID
      const userScope = import.meta.env.VITE_SLACK_SLACK_USER_SCOPES
      const botScope = import.meta.env.VITE_SLACK_SLACK_SCOPES
      const redirectUri = `${redirectUriBase}/integrations/slack/callback`
      const stateParam = state

      let params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        state: stateParam
      })

      if (type === "bot" || type === "link-bot") {
        params.set("scope", botScope)
        params.set("user_scope", userScope)
      } else {
        params.set("user_scope", userScope)
      }

      const authorizeUrl = `${baseUrl}?${params.toString()}`
      window.open(authorizeUrl, "_blank")
    },

    confirmClearServiceData() {
      IntegrationService.confirmServiceDataPurge(this.selected_service.reference.toLowerCase(), { pin_code: this.purge_pin_code })
        .then((res) => {
          const status = res.data.status
          if (status == "PURGE_STARTED") {
            this.show_confirm_purge_input_modal = false
            this.showNotification(
              this.$t("Data purge started"),
              this.$t("It might take couple of minutes to completely purge your revsetter") +
                ` ${this.selected_service.reference} ` +
                this.$t("data"),
              "success"
            )
          } else {
            this.showNotification(this.$t("Invalid or expired token"), ``, "error")
          }
        })
        .catch(() => {
          this.showNotification(this.$t("Invalid or expired token"), ``, "error")
        })
    },
    linkService(service, not_a_popup) {
      if (this.paidSub) {
        this.subscribeToScribe(`${import.meta.env.VITE_BASE_URL}/admin/integrations?integration=${service.reference}`)
      } else {
        if (service.reference == "spreadsheet") {
          this.handleAddIntegrations(service, "upload", not_a_popup)
        } else if (
          this.$utils.isNilUUID(service.id) ||
          ((service.status == "off" || service.status == "revoked") && service.integration_provider == "mergelink")
        ) {
          this.handleAddIntegrations(service, "auth", not_a_popup)
        } else {
          this.handleAddIntegrations(service, "re-auth", not_a_popup)
        }
      }
    },
    addNewInstance(service) {
      this.handleAddIntegrations(service, "add")
    },
    reAuth(service) {
      this.handleAddIntegrations(service, "re-auth")
    },
    adjustMapping(service) {
      this.handleAddIntegrations(service, "mapping")
    },
    async addGoogleService(service, action = "auth") {
      let ser_id = service.id
      if (service.id == "00000000-0000-0000-0000-000000000000" || action == "add") {
        try {
          const response = await IntegrationService.linkService(service.reference, {
            integration_provider: "revsetter",
            instance_number:
              this.available_services.filter(
                (ser) => ser.service_provider == "Google" && ser.reference == "gmail" && ser.id != "00000000-0000-0000-0000-000000000000"
              ).length + 1
          })
          ser_id = response.data.id
        } catch (error) {
          console.error("Error linking service:", error)
          return
        }
      }
      // Proceed with OAuth only after successful linking
      let state = btoa(
        JSON.stringify({
          next: `/admin/integrations/`,
          integration_id: ser_id,
          org_id: this.$store.state.organization.id,
          integration_reference: service.reference
        })
      )
      let current_scopes = this.available_services
        .filter((ser) => ser.service_provider == "Google" && ser.status == "on")
        .map((ser) => ser.scope)
        .join(" ")

      let desired_scope = Array.from(new Set(`${current_scopes} ${service.required_scopes.join(" ")}`.split(" "))).join(" ")

      googleSdkLoaded((google) => {
        google.accounts.oauth2
          .initCodeClient({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
            scope: desired_scope,
            access_type: "offline",
            select_account: true,
            ux_mode: "redirect",
            redirect_uri: `${window.location.origin}/integrations/${service.reference}/callback`,
            state: state
          })
          .requestCode()
      })
    },
    handleAddIntegrations(service, action = "auth", not_a_popup) {
      // action cane be  "auth", "mapping","re-auth", "add"
      if (service.service_provider == "Google") {
        return this.addGoogleService(service, action)
      } else if (service.service_provider == "Microsoft") {
        let ser_id = service.id
        if (service.id == "00000000-0000-0000-0000-000000000000" || action == "add") {
          ser_id = IntegrationService.linkService(service.reference, {
            integration_provider: "revsetter",
            instance_number:
              this.available_services.filter(
                (ser) =>
                  ser.service_provider == "Microsoft" && ser.reference == "outlook" && ser.id != "00000000-0000-0000-0000-000000000000"
              ).length + 1
          })["id"]
        }
        let state = btoa(
          JSON.stringify({
            next: `/admin/integrations/`,
            integration_id: ser_id,
            org_id: this.$store.state.organization.id,
            integration_reference: service.reference
          })
        )
        getAuthUrl(service.reference, {
          state: state
        }).then((auth_url) => {
          window.open(auth_url, not_a_popup ? "_self" : "_blank")
        })
      } else if (service.reference == "whatsapp") {
        // this.show_prefill_whatsapp_business_modal = true
        this.createNewWhatsAppBusinessAccount()
      } else if (service.service_provider == "Slack") {
        IntegrationService.linkService(service.reference, {
          integration_provider: "revsetter"
        }).then(({ data }) => {
          let state = btoa(
            JSON.stringify({
              next: `/admin/integrations/`,
              integration_id: data.id,
              org_id: this.$store.state.organization.id,
              integration_reference: service.reference
            })
          )

          if (service.scopes.includes("chat:write")) {
            this.linkSlack(service, "bot", state)
          } else {
            this.linkSlack(service, "user", state)
          }
        })
      } else {
        let integration_route = "/admin/integrations"
        if (service.integration_provider === "revsetter") {
          if (action == "re-auth") {
            if (service.reference == "salesforce") {
              let subdomain = (import.meta.env.VITE_IS_SANDBOX == "true") == "true" ? "test" : "login"
              let authorize_url = `https://${subdomain}.salesforce.com/services/oauth2/authorize?response_type=code&client_id=${
                import.meta.env.VITE_SALESFORCE_CLIENT_ID
              }&redirect_uri=${window.location.origin}/integrations/salesforce/callback`
              // base64
              authorize_url += `&state=${btoa(
                JSON.stringify({
                  next: `/admin/integrations/salesforce/${service.id}/object-selection`,
                  integration_id: service.id,
                  integration_reference: service.reference
                })
              )}`
              window.open(authorize_url, not_a_popup ? "_self" : "_blank")
            } else {
              if (service.category == "database") {
                integration_route += `/${service.reference}/${service.id}/authorization`
              } else if (service.reference == "whatsapp") {
                this.linkExistingWhatsAppAccount()
              } else {
                getAuthUrl(service.reference, {
                  state: btoa(
                    JSON.stringify({
                      next: `/admin/integrations/${service.reference}/${service.id}/object-selection`,
                      integration_id: service.id,
                      integration_reference: service.reference
                    })
                  ),
                  use_sandbox: service.reference == "salesforce" && import.meta.env.VITE_IS_SANDBOX == "true"
                }).then((auth_url) => {
                  window.open(auth_url, not_a_popup ? "_self" : "_blank")
                })
              }
            }
          } else if (action == "upload") {
            integration_route += `/${service.reference}/spreadsheet/upload`
          } else if (action == "re-upload") {
            integration_route += `/${service.reference}/${service.id}/spreadsheet/upload`
          } else if (action == "mapping") {
            integration_route += `/${service.reference}/${service.id}/object-selection`
          } else if (action == "add") {
            let linked_instances_count =
              this.available_services.filter((s) => s.reference == service.reference && !this.$utils.isNilUUID(s.id)).length + 1
            integration_route += `/${service.reference}/${service.reference}/link?instance_number=${linked_instances_count}`
          } else if (action == "auth") {
            if (service.category == "database") {
              IntegrationService.linkService(service.reference, {
                integration_provider: "revsetter"
              }).then(({ data }) => {
                integration_route += `/${service.reference}/${data.id}/authorization`
                this.$router.push(integration_route)
              })
            } else {
              IntegrationService.linkService(service.reference, {
                integration_provider: "revsetter"
              }).then(({ data }) => {
                getAuthUrl(service.reference, {
                  state: btoa(
                    JSON.stringify({
                      next: `/admin/integrations/${service.reference}/${data.id}/object-selection`,
                      integration_id: data.id,
                      integration_reference: service.reference
                    })
                  ),
                  use_sandbox: service.reference == "salesforce" && import.meta.env.VITE_IS_SANDBOX == "true"
                }).then((auth_url) => {
                  window.open(auth_url, not_a_popup ? "_self" : "_blank")
                })
                return
              })
            }
          } else {
            throw Error("Invalid action")
          }
        } else if (service.integration_provider === "mergelink") {
          if (action == "mapping") {
            integration_route += `/${service.reference}/${service.id}/object-selection`
          } else if (action == "re-auth") {
            this.addMergelinkService(service)
            return
          } else if (action == "auth") {
            this.linkMergelinkService(service)
            return
          } else if (action == "add") {
            let instance_number = this.available_services.filter((s) => s.reference == service.reference).length + 1
            this.linkMergelinkService(service, instance_number)
            return
          }
        } else {
          throw Error("Invalid integration provider")
        }

        this.$router.push(integration_route)
      }
    },
    linkMergelinkService(service, instance_number = 1) {
      IntegrationService.linkService(service.reference, {
        integration_provider: "mergelink",
        instance_number: instance_number
      })
        .then((response) => {
          this.$store.dispatch("ui/hideLoading")
          this.addMergelinkService(response.data)
        })
        .catch(() => {
          this.$store.dispatch("ui/hideLoading")
        })
    },
    addMergelinkService(service) {
      // create the session and send the user to authenticate
      this.$store.dispatch("ui/showLoading")
      let webhooks_config = this.getMergelinkWebhookParams(service)
      let data = {
        service_name: "mergelink",
        service_endpoint: "create_session", //init pull
        integration_name: this.integration_name,
        data: {
          schema: getSchema(this.integration_name),
          provider: this.integration_name,
          save_session: true,
          field_selection: false,
          resource_selection: true,
          sync: false,
          passthrough_fields: true,
          schema_selection: false,
          redirect_url: window.location.origin + `/admin/integrations/${this.integration_name}/${service.id}/object-selection`,
          configuration_webhook: webhooks_config["configuration_webhook"],
          completed_webhook: webhooks_config["completed_webhook"],
          data_webhook: webhooks_config["data_webhook"],
          error_webhook: webhooks_config["error_webhook"]
        }
      }
      IntegrationService.callExternalServiceEndpoint(data)
        .then((res) => {
          this.$store.dispatch("ui/hideLoading")
          window.open(`https://endgrate.com/pull?session_id=${res.data.session_id}`, "_blank")
        })
        .catch(() => {
          this.$store.dispatch("ui/hideLoading")
        })
    },
    getMergelinkWebhookParams(service) {
      let webhook_url = `${import.meta.env.VITE_BASE_API_URL}/webhooks/mergelink/${this.integration_name}/`

      // if in dev mode and ngrok url is present then use it
      if (import.meta.env.MODE == "development" && import.meta.env.NGROK_URL) {
        webhook_url = `${import.meta.env.NGROK_URL}/webhooks/mergelink/${this.integration_name}/`
      }
      return {
        configuration_webhook: {
          endpoint: webhook_url,
          metadata: {
            user_id: this.$store.state.auth.user.id,
            event_type: "configuration"
          }
        },
        completed_webhook: {
          endpoint: webhook_url,
          metadata: {
            id: service.id,
            user_id: this.$store.state.auth.user.id,
            event_type: "transfer"
          }
        },
        data_webhook: {
          endpoint: webhook_url,
          continuous: false,
          metadata: {
            id: service.id,
            user_id: this.$store.state.auth.user.id,
            init_transfer: true,
            event_type: "data"
          }
        },
        error_webhook: {
          endpoint: webhook_url,
          metadata: {
            id: service.id,
            user_id: this.$store.state.auth.user.id,
            event_type: "error"
          }
        }
      }
    },
    resetFile() {
      this.current_file = ""
    },
    // Helper Functions
    showLoading(message = "") {
      this.$store.dispatch("ui/showLoading", message)
    },
    hideLoading() {
      this.$store.dispatch("ui/hideLoading")
    },
    selectService(service) {
      this.selected_service = service
    },
    showToast(message, type, timeout = 3000) {
      this.$toast.open({
        message: message,
        position: "top",
        type: type,
        duration: timeout
      })
    },
    runServiceAction(service, actionFunction, callback = null) {
      actionFunction(service.id)
        .then(() => {
          this.getIntegratedServices()
          this.hideLoading()
          if (callback) {
            callback()
          }
        })
        .catch(() => {
          this.getIntegratedServices()
          this.hideLoading()
        })
    },
    getIntegratedServices() {
      this.showLoading()
      IntegrationService.allServices()
        .then(({ data }) => {
          this.available_services = data
          this.hideLoading()
          this.refreshServicesRealtimeUpdatesStatus()
        })
        .catch(() => {
          this.hideLoading()
        })
    },
    showNotification(title, description, type) {
      this.showToast(`${title}\n ${description}`, type)
    },
    abortJob(service, job) {
      IntegrationService.abortSyncJob(service.id, job.id).then(() => {
        this.showToast(this.$t("Job has been aborted"), "success")
        this.refetchService(service)
      })
    },
    reRunJob(service, job) {
      IntegrationService.reRunSyncJob(service.id, job.id).then(() => {
        this.showToast(this.$t("Job has been re-run"), "success")
        this.refetchService(service)
      })
    },
    submitWhatsAppBusinessPrefillDetails(values) {
      const setup_data = {
        business: {
          name: values.business_name,
          email: values.business_email,
          website: values.business_website,
          address: {
            streetAddress1: values.business_address
          },
          phone: {
            code: values.country_code,
            number: values.business_phone_number
          }
        }
      }
      this.createNewWhatsAppBusinessAccount(setup_data)
      this.show_prefill_whatsapp_business_modal = false
    }
  }
}
</script>

<style>
.custom-file-input {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  border: none;
  background-color: transparent;
  resize: none;
  outline: none;
  cursor: pointer;
}

/* Custom scrollbar styling */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
